<!--
    This component is intended primarily to hold logic for selecting and de-selecting
    from an array of comparable objects.

    The default slot values provide a 'quick implementation', which is affected but should
    be tweaked a little, in most cases to provide added functionality.

    Create specific implementations in their own components, rather than leaving
    naked <GenericSelector /> components in the views templates.

    See:
        - src/components/CompetencyTests/TestSelector
        - src/components/FormResponses/FormSelector
-->
<template>
    <div>
        <!-- Selected Items -->
        <slot name="selectedHeader" />

        <!-- Skeleton Loader -->
        <slot v-if="!disableSkeletonLoader" name="skeletonLoader">
            <table v-if="loading" class="w-100">
                <tr v-for="item in Array(5)" v-bind:key="item">
                    <td><div class="skeleton midnight h16 w-100 rounded mb-1"></div></td>
                    <td><div class="skeleton midnight h16 w-100 rounded mb-1"></div></td>
                </tr>
            </table>
        </slot>
        <div v-if="innerValue && innerValue.length && !loading" :class="internalSelectedWrapperClass">
            <!-- optional header entry -->
            <slot name="selectedTop" />

            <!-- items -->
            <template v-if="!hideSelected">
            <div
                v-for="val in innerValue"
                :key="val[compareKey]"
                :class="internalSelectedItemClass"
                @click.stop="handleClick(val)"
                :style="canEdit ? 'cursor: pointer;' : ''"
            >
                <slot name="selected" :option="val">
                    <b-icon v-if="showOptions" icon="dash-circle-fill" class="float-right ml-2 mt-0 mb-0" />{{ displayCb(val) }}
                    <b-badge v-if="allowBadges" variant="primary" class="float-right"
					>{{ badgeCb(val) }}</b-badge
				>
                </slot>
            </div>
            </template>

        </div>
        <slot v-else-if="!disableNoneSelectedAlert && !loading" name="noneSelected">
            <div class="alert alert-light">{{ $t('none-selected') }}</div>
        </slot>


        <!-- Unselected Options -->
        <slot name="unselectedHeader">
            <div class="mb-3" />
        </slot>

        <div v-if="showOptions" :class="internalOptionWrapperClass">
            <!-- optional header entry -->
            <slot name="unselectedTop" />

            <!-- remaining options -->
            <div
                v-for="option in unselected"
                :key="option[compareKey]"
                :class="internalOptionItemClass"
                @click.stop="select(option)"
                :style="canEdit ? 'cursor: pointer;' : ''"
            >
                <slot name="unselected" :option="option">
                    <b-icon v-if="showOptions" icon="plus-circle-fill" class="float-right ml-2 mt-0 mb-0" />{{ displayCb(option) }}
                </slot>
            </div>
        </div>

        <slot name="footer" />
    </div>
</template>

<script>
export default {
    props: {
        value: {
            type: Array,
            default: () => [],
        },
        options: {
            type: Array,
            required: true,
        },
        showOptions: {
            type: Boolean,
            required: true,
        },
        canEdit: {
            type: Boolean,
            default: true,
        },
        allowBadges: {
            type: Boolean,
            default: false,
        },
        loading: {
            type: Boolean,
            default: false,
        },
        // Over-ride if ID field has a different name to 'id' (E.g. 'Id', 'type_id', ...)
        compareKey: {
            type: String,
            default: 'id',
        },
        // Used to compare objects when toggling selected.
        // Over-ride if a more complex comparison procedure is required.
        compareCb: {
            type: Function,
            default: function (val0, val1) {
                return val0[this.compareKey] === val1[this.compareKey]
            }
        },
        // Field name to display default row info with - convenience prop which can be
        // ignored if using templating to display select items.
        displayKey: {
            type: String,
            default: 'title',
        },
        // Again, can be over-ridden if more complex behaviour desired to display rows
        displayCb: {
            type: Function,
            default: function (val) {
                return val[this.displayKey]
            },
        },
        badgeCb: {
            type: Function,
            default: function (val) {
                return val[this.displayKey]
            },
        },
        // Over-ride the class used to wrap the selected list
        selectedWrapperClass: {
            type: String,
            default: 'list-group border-radius-2 no-border',
        },
        // Over-ride the class used to wrap the selected item
        selectedItemClass: {
            type: String,
            default: 'list-group-item',
        },
        // Over-ride the class used to wrap the option list
        optionWrapperClass: {
            type: String,
            default: 'list-group border-radius-2 no-border',
        },
        // Over-ride the class used to wrap the option items
        optionItemClass: {
            type: String,
            default: 'list-group-item',
        },
        // Choose from a handful of default presentation styles for this item
        // "list": this is the default style
        // "tag": this present as a "tag" style input selector
        // "none": this removes all styles so you can create your own using slots
        selectorStyle: {
            type: String,
            default: 'list',
        },
        disableNoneSelectedAlert: {
            type: Boolean,
            default: false,
        },
        disableSkeletonLoader: {
            type: Boolean,
            default: false,
        },
        // Useful if the selected options are displayed some other way in the UI
        hideSelected: {
            type: Boolean,
            default: false,
        },
        // Custom behaviour when clicking on an option and viewOptions is false
        clickCb: {
            type: Function,
            default: function (val) {
                return val[this.displayKey]
            },
        },
        // This was the only way to get the stations buttons working for the OSCE lists.
		stations: {
			type: Array,
			default: () => []
		}
    },
    mounted() {
        if (this.selectorStyle == 'none') {
            this.internalSelectedWrapperClass = '';
            this.internalSelectedItemClass = '';
            this.internalOptionWrapperClass = '';
            this.internalOptionItemClass = '';
        } else if(this.selectorStyle == 'tag') {
            this.internalSelectedWrapperClass = 'pseudo-input';
            this.internalSelectedItemClass = 'btn btn-primary mr-1 mb-1 text-left';
            this.internalOptionWrapperClass = '';
            this.internalOptionItemClass = 'btn btn-outline-secondary mr-1 mb-1 text-left';
        }
    },
    data() {
        return {
            innerValue: [],
            internalSelectedWrapperClass: this.selectedWrapperClass,
            internalSelectedItemClass: this.selectedItemClass,
            internalOptionWrapperClass: this.optionWrapperClass,
            internalOptionItemClass: this.optionItemClass,
        }
    },
    methods: {
        select(option) {
            if (!this.canEdit) return

            this.innerValue.push(option)
            this.$emit('input', this.innerValue)
        },
        unselect(option) {
            if (!this.canEdit) return

            this.innerValue.splice(this.innerValue.findIndex(val => this.compareCb(option, val)), 1)
            this.$emit('input', this.innerValue)
        },
        handleClick(option) {
            if (!this.canEdit) return;

            if (this.showOptions) {
                this.unselect(option);
            } else {
                this.clickCb(option); // Emit custom event
            }
        }
    },
    computed: {
        unselected() {
            return this.options.filter(option => this.innerValue?.findIndex(val => this.compareCb(option, val)) === -1)
        }
    },

    watch: {
        value: {
            immediate: true,
            handler: function (newVal) {
                this.innerValue = newVal
            },
        },
    }
}
</script>
